import React, { useState } from 'react';
import { usePaidAssessment } from '../utils/usePublicConfig';
import { Link } from 'react-router-dom';
import {
  ArrowRight, CheckCircle2, XCircle, Calendar, ClipboardList, Dumbbell,
  LineChart, Mail, Phone, MapPin, ChevronDown, Users, Info, Star,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import BookingModal from '../components/BookingModal';
import { useDocumentMeta } from '../utils/useDocumentMeta';
import { trackLead } from '../utils/metaPixel';

const FOR_WHOM = [
  'Kezdőknek, vagy akik hosszú kihagyás után térnének vissza',
  'Műszakban vagy ülőmunkában dolgozóknak, akiknek nehéz beilleszteni a mozgást',
  'Akik nem villámfogyókúrát akarnak, hanem tartós változást',
  'Akik szeretnék végre megtanulni a helyes technikát, hogy egyedül is magabiztosan eddzenek',
];

const PERSONAL_FEATURES = [
  'Kezdő és záró állapotfelmérés (ingyenes), egészségügyi kérdőív',
  'Heti 2 edzés: 1 közös velem a BB Gymben (összesen 8), 1 önálló az egyéni terved alapján',
  'Rövid visszajelzés az önálló edzésekről, hogy lássam, hogyan haladsz',
  'Étrendi alapelvek és mintanapok a saját napi ritmusodhoz',
  'Kérdések munkanapokon, 24 órán belüli válasszal',
  'Záró értékelés és terv a folytatáshoz',
];

const ONLINE_FEATURES = [
  'Felmérés és egészségügyi kérdőív',
  'Heti 2 edzés az egyéni terved alapján, teremben vagy otthon',
  'Heti videós technikaellenőrzés',
  'Kérdések munkanapokon, 24 órán belüli válasszal',
  'Záró értékelés és terv a folytatáshoz',
];

const STEPS = [
  {
    icon: Calendar,
    title: 'Ingyenes 20 perces konzultáció',
    text: 'Személyesen a BB Gymben vagy online. Megbeszéljük a céljaidat, és eldöntjük, neked való-e a program.',
  },
  {
    icon: ClipboardList,
    title: 'Ingyenes állapotfelmérés',
    text: 'Személyesen a BB Gymben, díjmentesen megnézzük, honnan indulsz: testtartás, mozgás, erő, életmód.',
  },
  {
    icon: Dumbbell,
    title: '8 hét közös munka',
    text: 'Hetente két edzés: egy velem, egy önállóan a terved alapján. Az önálló edzés után rövid visszajelzést küldesz, és közben bármikor kérdezhetsz.',
  },
  {
    icon: LineChart,
    title: 'Záró felmérés',
    text: 'Számokban is látod, mit értél el, és kapsz egy tervet a folytatáshoz.',
  },
];

const FAQ = [
  {
    q: 'Teljesen kezdő vagyok. Nem leszek ciki a teremben?',
    a: 'Pont ezért van a program. Az első alkalmakon megtanuljuk az alapgyakorlatokat, úgyhogy mire egyedül edzel, tudni fogod, mit csinálsz.',
  },
  {
    q: 'Miért csak heti egy közös edzés van?',
    a: 'A program heti 2 edzésre épül, mert kezdőként ez hozza a legjobb eredményt. A közös alkalmon megtanulod a technikát és a következő hét feladatait, a másodikat önállóan csinálod. Így a 8 hét végére nemcsak fittebb leszel, hanem egyedül is tudni fogod, mit csinálj, és az ár is elérhető marad.',
  },
  {
    q: 'Műszakban dolgozom, változó időpontokban. Ez működik?',
    a: 'Igen. Az edzéseket a beosztásodhoz igazítjuk, a terem hétköznap reggel 5-től este 9-ig nyitva van.',
  },
  {
    q: 'Jöhetek egy barátommal vagy a párommal?',
    a: 'Igen! 2-3 fős csoportban kedvezményesebb az ár. A felmérés és az edzésterv mindenkinek egyéni, az edzések közösek. Egy közös időpontot választunk a teljes 8 hétre. Ha valaki egy alkalommal nem tud jönni, az edzést a többiekkel megtartjuk.',
  },
  {
    q: 'Lesz étrendem is?',
    a: 'Nem szigorú diétát kapsz, hanem alapelveket és mintanapokat, amiket a saját életedben is tudsz alkalmazni. Ha orvosi vagy speciális étrendre van szükséged, dietetikushoz irányítalak.',
  },
  {
    q: 'Mi van, ha kihagyok egy edzést?',
    a: 'Előre jelezve áttehetjük. A részletes lemondási szabályokat az ÁSZF tartalmazza.',
  },
  {
    q: 'Online is lehet a konzultáció?',
    a: 'Igen, az ingyenes 20 perces konzultáció lehet személyesen a BB Gymben vagy online videóhívásban. Az állapotfelmérés a személyes és csoportos változatnál a teremben történik, az első közös alkalom előtt.',
  },
];

// Szó szerint a valódi Google-értékelésekből (lásd components/ReviewsSection.jsx).
const REVIEWS = [
  {
    name: 'Miklós',
    content:
      'Szakmailag nagyon felkészült, és ami nekem legalább ennyire fontos, az a hozzáállása, a folyamatos támogatása és a hasznos, személyre szabott tanácsai. […] Az elmúlt körülbelül két és fél hónap alatt 11 kilót sikerült leadnom, és sokkal fittebbnek is érzem magam.',
  },
  {
    name: 'Patricia',
    content:
      'Kedves, türelmes, értelmesen magyaráz. […] Én az online coachingot választottam, és nem bántam meg. Egyszerű a felület használata, és külön pont, hogy a fejlődésemet is láthatom.',
  },
];

const RULES = [
  'a díjat a kezdés előtt fizesd ki (két részletben is lehet, az első a kezdés előtt esedékes),',
  'vegyél részt az edzéseken és a kezdő, illetve záró felmérésen,',
  'a végén írj egy őszinte Google-értékelést.',
];

const CtaButton = ({ onClick, testId, className = '' }) => (
  <Button
    data-testid={testId}
    onClick={onClick}
    className={`bg-yellow-500 hover:bg-yellow-400 text-gray-950 font-black tracking-wide h-14 px-8 text-sm sm:text-base rounded-lg transition-all duration-200 hover:shadow-xl hover:shadow-yellow-500/25 ${className}`}
  >
    FOGLALJ INGYENES KONZULTÁCIÓT
    <ArrowRight size={18} className="ml-2" strokeWidth={3} />
  </Button>
);

const SectionTitle = ({ children, eyebrow }) => (
  <>
    {eyebrow && (
      <div className="text-[11px] font-bold tracking-[0.26em] text-yellow-500 mb-3">{eyebrow}</div>
    )}
    <h2
      className="text-2xl sm:text-3xl font-black text-white mb-5"
      style={{ fontFamily: 'Montserrat, sans-serif' }}
    >
      {children}
    </h2>
  </>
);

const AlapozoPage = () => {
  const paidAssessment = usePaidAssessment();
  // A felmérés ingyenes szövegei visszaállnak, ha a fizetős mód be van kapcsolva.
  const steps = STEPS.map((s) => (paidAssessment && s.title === 'Ingyenes állapotfelmérés'
    ? { ...s, title: 'Állapotfelmérés', text: 'Személyesen a BB Gymben megnézzük, honnan indulsz: testtartás, mozgás, erő, életmód.' }
    : s));
  const includes = PERSONAL_FEATURES.map((t) => (paidAssessment
    ? t.replace('Kezdő és záró állapotfelmérés (ingyenes)', 'Kezdő és záró állapotfelmérés')
    : t));
  useDocumentMeta({
    title: 'Alapozó Program – Személyi edzés Miskolc | Orosz Coaching',
    description:
      '8 hetes kezdő edzésprogram Miskolcon, a BB Gymben: felmérés, egyéni terv, heti 2 edzés, ebből egy személyesen az edzővel. Egyénileg vagy kiscsoportban. Csak 10 hely, jelentkezés november 15-ig.',
  });

  const [bookingOpen, setBookingOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  const openBooking = (where) => {
    trackLead({ content_name: `alapozo_${where}` });
    setBookingOpen(true);
  };

  return (
    <div data-testid="alapozo-page" className="min-h-screen bg-[#0B1B33] text-white">
      {/* Fejléc — csak a logó, ami a főoldalra visz */}
      <header className="border-b border-white/10">
        <div className="container mx-auto px-6 py-5">
          <Link
            to="/"
            data-testid="alapozo-logo"
            className="inline-block text-xl font-bold tracking-tight"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            <span className="text-white">OROSZ</span>
            <span className="text-yellow-500 ml-1">COACHING</span>
          </Link>
        </div>
      </header>

      <main>
        {/* HERO */}
        <section className="container mx-auto px-6 pt-14 pb-16 sm:pt-20 sm:pb-20 max-w-4xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-yellow-500/40 bg-yellow-500/10 text-yellow-400 text-[11px] font-bold uppercase tracking-widest px-3 py-1.5 mb-6">
            BB Gym · Miskolc
          </div>
          <h1
            data-testid="alapozo-heading"
            className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.03]"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Alapozó Program
          </h1>
          <p className="mt-5 text-base md:text-lg text-yellow-500 font-bold leading-snug">
            8 hét, ami után tudod, mit csinálsz a teremben, és miért
          </p>
          <p className="mt-6 text-base text-white/75 leading-relaxed">
            Évek óta nem mozogtál? Műszakban dolgozol, vagy egész nap ülsz? Belekezdtél már
            párszor, de októberre mindig elfogyott a lendület?
          </p>
          <p className="mt-4 text-base text-white/75 leading-relaxed">
            Az Alapozó Program arra való, hogy 8 hét alatt felépítsünk egy olyan edzési rutint, ami
            a te életedbe illeszkedik. Hetente kétszer edzel: egyszer velem, egyszer önállóan a
            terved alapján. Így nemcsak edzel, hanem megtanulsz egyedül is magabiztosan edzeni.
          </p>

          <div className="mt-8 rounded-xl border border-yellow-500/35 bg-yellow-500/10 p-5">
            <p className="text-sm sm:text-base font-bold text-white leading-relaxed">
              Csak 10 helyet indítok. Jelentkezés: november 15-ig. Kezdés: november második felében.
            </p>
          </div>

          <div className="mt-8">
            <CtaButton onClick={() => openBooking('hero')} testId="alapozo-cta-hero" className="w-full sm:w-auto" />
          </div>
        </section>

        {/* KINEK SZÓL / NEM SZÓL */}
        <section className="border-t border-white/10 bg-white/[0.02]">
          <div className="container mx-auto px-6 py-16 max-w-4xl">
            <SectionTitle>Kinek szól?</SectionTitle>
            <ul className="space-y-3 mb-12">
              {FOR_WHOM.map((t) => (
                <li key={t} className="flex gap-3 text-sm sm:text-base text-white/80 leading-relaxed">
                  <CheckCircle2 size={19} className="text-yellow-500 shrink-0 mt-0.5" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>

            <SectionTitle>Kinek nem szól?</SectionTitle>
            <div className="flex gap-3 text-sm sm:text-base text-white/70 leading-relaxed">
              <XCircle size={19} className="text-red-400 shrink-0 mt-0.5" />
              <p>
                Annak, aki csodát vár 8 hét alatt, vagy aki nem tud heti legalább 2 alkalmat
                rászánni. Őszintén megmondom a konzultáción, ha nem ez a jó megoldás neked.
              </p>
            </div>
          </div>
        </section>

        {/* KI VAGYOK? */}
        <section className="border-t border-white/10">
          <div className="container mx-auto px-6 py-16 max-w-5xl">
            <div className="grid md:grid-cols-[300px_minmax(0,1fr)] gap-8 md:gap-12 items-center">
              <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-black/40 max-w-[300px] w-full mx-auto md:mx-0">
                <picture>
                  <source srcSet="/orosz-gabor-about-800.webp" type="image/webp" />
                  <img
                    src="/orosz-gabor-about-v2.jpg"
                    alt="Orosz Gábor testépítés-fitnesz edző, BB Gym Miskolc"
                    data-testid="alapozo-about-photo"
                    className="w-full h-full object-cover object-[center_20%] aspect-[4/5]"
                    loading="lazy"
                    decoding="async"
                  />
                </picture>
              </div>
              <div>
                <SectionTitle eyebrow="AZ EDZŐD">Ki vagyok?</SectionTitle>
                <p className="text-sm sm:text-base text-white/75 leading-relaxed -mt-1">
                  Orosz Gábor vagyok, államilag elismert testépítés-fitnesz edző a miskolci BB
                  Gymben. 14 éve edzek, és én is műszakos munka mellett kezdtem, úgyhogy tudom,
                  milyen nehéz beilleszteni a mozgást egy fárasztó hét mellé. Nem csodát ígérek,
                  hanem egy tudományosan megalapozott, a te életedhez igazított tervet, és azt,
                  hogy végig melletted leszek.
                </p>
              </div>
            </div>

            {/* VÉLEMÉNYEK */}
            <div className="grid sm:grid-cols-2 gap-4 mt-12" data-testid="alapozo-reviews">
              {REVIEWS.map((r) => (
                <div
                  key={r.name}
                  data-testid={`alapozo-review-${r.name.toLowerCase()}`}
                  className="rounded-2xl border border-white/10 bg-white/[0.04] p-5"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <Star key={i} size={14} className="text-yellow-500 fill-yellow-500" />
                    ))}
                  </div>
                  <p className="text-[13px] sm:text-sm text-white/80 leading-relaxed">{r.content}</p>
                  <div className="mt-4 flex items-center gap-2 text-xs">
                    <span className="font-bold text-white">{r.name}</span>
                    <span className="text-white/25">·</span>
                    <span className="text-white/50">Google-értékelés</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ÁRAK */}
        <section className="border-t border-white/10">
          <div className="container mx-auto px-6 py-16 max-w-5xl">
            <SectionTitle eyebrow="ÁRAK">Válaszd ki, neked melyik való</SectionTitle>

            <div className="grid md:grid-cols-2 gap-5 mt-8">
              {/* Személyes */}
              <div
                data-testid="alapozo-price-personal"
                className="relative rounded-2xl border-2 border-yellow-500 bg-white/[0.04] p-6 pt-8"
              >
                <div className="absolute -top-3 left-6 bg-yellow-500 text-gray-950 text-[10px] font-black tracking-[0.16em] px-3 py-1.5 rounded">
                  LEGNÉPSZERŰBB
                </div>
                <h3 className="text-xl font-black text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Személyes
                </h3>
                <div className="mt-3 mb-5">
                  <div className="text-3xl font-black text-yellow-500" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    45 000 Ft <span className="text-base text-white/60 font-bold">/ 8 hét</span>
                  </div>
                  <div className="text-xs text-white/50 mt-1">havonta kb. 22 500 Ft</div>
                  {!paidAssessment && (
                    <div
                      data-testid="alapozo-free-assessment-badge"
                      className="mt-3 inline-flex items-center gap-2 rounded-full bg-emerald-600 px-3.5 py-1.5 text-[11px] font-black uppercase tracking-wide text-white"
                    >
                      <CheckCircle2 size={14} />
                      Ingyenes állapotfelmérés · 0 Ft
                    </div>
                  )}
                </div>
                <ul className="space-y-2.5">
                  {includes.map((f) => (
                    <li key={f} className="flex gap-2.5 text-[13px] text-white/80 leading-snug">
                      <CheckCircle2 size={16} className="text-yellow-500 shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Online */}
              <div
                data-testid="alapozo-price-online"
                className="rounded-2xl border border-white/15 bg-white/[0.03] p-6 pt-8"
              >
                <h3 className="text-xl font-black text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Online
                </h3>
                <div className="mt-3 mb-5">
                  <div className="text-3xl font-black text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    25 000 Ft <span className="text-base text-white/60 font-bold">/ 8 hét</span>
                  </div>
                  {!paidAssessment && (
                    <div
                      data-testid="alapozo-free-assessment-badge-online"
                      className="mt-3 inline-flex items-center gap-2 rounded-full bg-emerald-600 px-3.5 py-1.5 text-[11px] font-black uppercase tracking-wide text-white"
                    >
                      <CheckCircle2 size={14} />
                      Ingyenes állapotfelmérés · 0 Ft
                    </div>
                  )}
                </div>
                <ul className="space-y-2.5">
                  {ONLINE_FEATURES.map((f) => (
                    <li key={f} className="flex gap-2.5 text-[13px] text-white/80 leading-snug">
                      <CheckCircle2 size={16} className="text-white/50 shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Csoportos doboz */}
            <div
              data-testid="alapozo-group-box"
              className="mt-5 rounded-2xl border border-emerald-400/40 bg-emerald-500/[0.08] p-6"
            >
              <div className="flex items-center gap-2 mb-3">
                <Users size={18} className="text-emerald-400" />
                <h3 className="text-lg font-black text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Párban vagy hárman jönnétek?
                </h3>
              </div>
              <p className="text-sm text-white/85 leading-relaxed">
                Csoportos változat: <strong className="text-emerald-300">30 000 Ft/fő (2 fő)</strong> vagy{' '}
                <strong className="text-emerald-300">25 000 Ft/fő (3 fő)</strong> a teljes 8 hétre.
              </p>
              <p className="text-sm text-white/75 leading-relaxed mt-2">
                Ugyanazt kapjátok, mint a személyes változatban: heti egy közös edzés velem, és heti
                egy önálló edzés a saját tervetek alapján. A felmérés és az edzésterv mindenkinek
                egyéni. Csak együtt jelentkező csoportoknak.
              </p>
              <p className="text-xs text-white/50 leading-relaxed mt-3 flex gap-2">
                <Info size={13} className="shrink-0 mt-0.5" />
                A BB Gym napijegy vagy bérlet nincs benne az árban. Ha már tag vagy, nincs más
                költséged.
              </p>
            </div>

            <div className="mt-8">
              <CtaButton onClick={() => openBooking('prices')} testId="alapozo-cta-prices" className="w-full sm:w-auto" />
            </div>
          </div>
        </section>

        {/* HOGYAN MŰKÖDIK */}
        <section className="border-t border-white/10 bg-white/[0.02]">
          <div className="container mx-auto px-6 py-16 max-w-4xl">
            <SectionTitle eyebrow="A FOLYAMAT">Hogyan működik?</SectionTitle>
            <ol className="space-y-5 mt-8" data-testid="alapozo-steps">
              {steps.map((s, i) => {
                const Icon = s.icon;
                return (
                  <li key={s.title} className="flex gap-4 sm:gap-5">
                    <div className="shrink-0 flex flex-col items-center">
                      <div className="w-11 h-11 rounded-full bg-yellow-500 text-gray-950 font-black flex items-center justify-center text-lg" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        {i + 1}
                      </div>
                      {i < STEPS.length - 1 && <div className="w-px flex-1 bg-white/15 mt-2" />}
                    </div>
                    <div className="pb-2">
                      <div className="flex items-center gap-2">
                        <Icon size={16} className="text-yellow-500" />
                        <h3 className="text-base font-bold text-white">{s.title}</h3>
                      </div>
                      <p className="text-sm text-white/70 leading-relaxed mt-1.5">{s.text}</p>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        </section>

        {/* SZABÁLYOK */}
        <section className="border-t border-white/10">
          <div className="container mx-auto px-6 py-16 max-w-4xl">
            <SectionTitle>A közös szabályaink</SectionTitle>
            <p className="text-sm sm:text-base text-white/75 leading-relaxed">
              Ez a program kedvezményes áron indul, mert most építem fel az ügyfélkörömet. Ezért
              cserébe azt kérem:
            </p>
            <ul className="mt-4 space-y-2.5">
              {RULES.map((r) => (
                <li key={r} className="flex gap-3 text-sm sm:text-base text-white/80 leading-relaxed">
                  <CheckCircle2 size={18} className="text-yellow-500 shrink-0 mt-0.5" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-white/60 leading-relaxed mt-4">
              Ha hozzájárulsz, az eredményeidet bemutathatom névvel vagy név nélkül, de ez nem
              feltétel.
            </p>
          </div>
        </section>

        {/* 8. HÉT UTÁN */}
        <section className="border-t border-white/10 bg-white/[0.02]">
          <div className="container mx-auto px-6 py-16 max-w-4xl">
            <SectionTitle>Mi lesz a 8. hét után?</SectionTitle>
            <p className="text-sm sm:text-base text-white/75 leading-relaxed">
              Folytathatod egyedül az elkészült terv alapján, vagy továbbléphetsz valamelyik
              programomba (Életmódváltó, Izomépítés, Kiscsoportos Duó &amp; Trió, Online mentorálás).
              A kezdő és a záró{' '}
              <strong className="text-yellow-500">állapotfelmérés mindkét alkalommal ingyenes</strong> —
              nincs rejtett költség.
            </p>
          </div>
        </section>

        {/* GYIK */}
        <section className="border-t border-white/10">
          <div className="container mx-auto px-6 py-16 max-w-4xl">
            <SectionTitle eyebrow="GYIK">Gyakori kérdések</SectionTitle>
            <div className="divide-y divide-white/10 border-y border-white/10" data-testid="alapozo-faq">
              {FAQ.map((item, i) => (
                <div key={item.q}>
                  <button
                    type="button"
                    data-testid={`alapozo-faq-q-${i}`}
                    onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                    className="w-full flex items-start justify-between gap-4 py-4 text-left group"
                  >
                    <span className="text-sm sm:text-base font-bold text-white group-hover:text-yellow-500 transition-colors">
                      {item.q}
                    </span>
                    <ChevronDown
                      size={18}
                      className={`shrink-0 mt-0.5 text-yellow-500 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {openFaq === i && (
                    <p
                      data-testid={`alapozo-faq-a-${i}`}
                      className="text-sm text-white/70 leading-relaxed pb-5 pr-8"
                    >
                      {item.a}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ZÁRÓ CTA */}
        <section className="border-t border-white/10 bg-gradient-to-b from-yellow-500/[0.07] to-transparent">
          <div className="container mx-auto px-6 py-16 max-w-4xl">
            <SectionTitle>Jelentkezz most</SectionTitle>
            <p className="text-sm sm:text-base text-white/75 leading-relaxed">
              Foglalj egy ingyenes 20 perces konzultációt — személyesen a BB Gymben vagy online, ahogy neked kényelmes — vagy írj nekem:
            </p>
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 text-sm">
              <a href="mailto:info@oroszcoaching.hu" data-testid="alapozo-email" className="inline-flex items-center gap-2 text-white hover:text-yellow-500 transition-colors">
                <Mail size={15} className="text-yellow-500" /> info@oroszcoaching.hu
              </a>
              <a href="tel:+36702378299" data-testid="alapozo-phone" className="inline-flex items-center gap-2 text-white hover:text-yellow-500 transition-colors">
                <Phone size={15} className="text-yellow-500" /> +36 70 237-8299
              </a>
            </div>
            <div className="mt-8">
              <CtaButton onClick={() => openBooking('footer')} testId="alapozo-cta-footer" className="w-full sm:w-auto" />
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/10">
        <div className="container mx-auto px-6 py-8">
          <p className="text-xs text-white/50 flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-bold text-white/70">Orosz Gábor</span>
            <span>testépítés-fitnesz edző</span>
            <span className="text-white/25">·</span>
            <span className="inline-flex items-center gap-1.5">
              <MapPin size={12} className="text-yellow-500" /> BB Gym, Miskolc, Szeles u. 71.
            </span>
          </p>
        </div>
      </footer>

      <BookingModal isOpen={bookingOpen} onClose={() => setBookingOpen(false)} />
    </div>
  );
};

export default AlapozoPage;
